// Data model for the US datasheet kit. Lives at sheet.us (stored inside product.data).
import { orderingPreset } from './orderingPresets';
import { catalogNumberFor, lumenTableFor, visibleOrderingGroups } from './orderingModel';
import { restoreOrderingOptions } from './orderingOptions';
import { US_FOOTER } from '../documentBranding';
export const VARIANTS = [
  { value: 'fixture', label: 'Trim / Fixture', pages: 'up to 5 pp' },
  { value: 'track', label: 'Track Fitting', pages: 'up to 5 pp' },
  { value: 'housing', label: 'Housing', pages: 'up to 2 pp' },
  { value: 'fitting-housing', label: 'Trim + Housing', pages: '5 + 2 pp' },
];
export const MODES = [{ value: 'submittal', label: 'Submittal' }, { value: 'catalogue', label: 'Catalogue' }];
export const BADGE_VARIANTS = [{ value: 'label', label: 'Label (ETL / LISTED)' }, { value: 'value', label: 'Value (IP / 44)' }, { value: 'warranty', label: 'Warranty (green)' }];
export const EXTRA_KINDS = [{ value: 'rows', label: 'Label / value rows' }, { value: 'bullets', label: 'Bullet list' }, { value: 'text', label: 'Paragraph' }];

export const hasFixture = v => v !== 'housing';
export const hasHousing = v => v === 'housing' || v === 'fitting-housing';
export const isTrack = v => v === 'track';
export const partLabel = v => (v === 'track' ? 'Fitting' : 'Trim');

const figure = (placeholder) => ({ src: '', alt: '', placeholder, caption: '', subcaption: '', title: '' });

export function defaultMeta() {
  const now = new Date();
  return {
    productName: '', family: '', productType: '', documentStatus: '',
    revision: `${String(now.getMonth() + 1).padStart(2, '0')}/${String(now.getFullYear()).slice(2)}`,
    year: String(now.getFullYear()),
    company: { ...US_FOOTER, tagline: 'Consult factory for custom options' },
    notes: { metric: 'Measurements in ( ) are metric equivalents.', changes: 'Specifications subject to change without notice.' },
  };
}

export function defaultFixture() {
  return {
    tags: [], band: [], catalogNumber: '', catalogNumberOverride: null, description: '', nominalAperture: '', baffle: '',
    productData: { title: 'PRODUCT DATA', rows: [] },
    compatible: { title: 'FOR USE WITH THE FOLLOWING HOUSINGS', items: [], note: '' },
    hero: figure('Product render / photo'),
    badges: [],
    features: { title: 'MAIN FEATURES', items: [] },
    specifications: [],
    dimensions: { drawings: [], rows: [] },
    ordering: { ...orderingPreset('downlight'), accessories: { title: 'ACCESSORIES — ORDERED SEPARATELY', columns: ['CODE', 'DESCRIPTION', 'NOTES'], template: '0.8fr 2.2fr 1.6fr', rows: [] } },
    photometrics: { intro: '', plots: [], lumenTable: { title: 'LUMEN OUTPUT + MULTIPLIERS', columns: ['MODEL', 'WATTAGE', 'NOMINAL LUMENS', 'LM/W'], template: '2.6fr 1.2fr 1.6fr 0.8fr', rows: [] }, footnote: '' },
    extras: { cover: [], specs: [], ordering: [], accessories: [], photometrics: [] },
  };
}

export function defaultHousing() {
  return {
    name: '', subtitle: 'RECESSED DOWNLIGHT · HOUSING & DRIVER', tags: [], band: [], catalogNumber: '', catalogNumberOverride: null, description: '',
    housingData: { title: 'HOUSING DATA', rows: [] },
    wattage: { title: '', items: [], note: '' },
    compatible: { title: 'FOR USE WITH THE FOLLOWING TRIMS', items: [], note: '' },
    hero: figure('Housing render'),
    badges: [],
    bulletGroups: [],
    drawings: { title: 'LINE DRAWINGS BY HOUSING STYLE', submittalNote: 'Specific submittal information.', intro: '', views: [], styles: [] },
    ordering: orderingPreset('housing'),
    extras: { cover: [], drawings: [], ordering: [] },
  };
}

// Track fittings ship on the same 5 fixture pages, with track-shaped content.
// Labels match the smart-code builder's field names so applying a code fills
// these rows instead of appending duplicates.
const TRACK_DATA_ROWS = ['ADAPTER', 'WATTAGE', 'NOMINAL LUMENS', 'CRI', 'COLOUR TEMPERATURE',
  'BEAM SPREAD', 'HOUSING FINISH', 'BAFFLE FINISH', 'VOLTAGE', 'DIMMING', 'WEIGHT', 'WARRANTY'];
const TRACK_SPEC_GROUPS = ['CONSTRUCTION', 'LED + OPTICS', 'ELECTRICAL', 'STANDARDS + WARRANTY'];

const sameLabels = (rows, labels) => (rows || []).length === labels.length
  && rows.every((r, i) => txt(r.label).toUpperCase() === labels[i] && !txt(r.value));
const emptyGroups = (groups, titles) => (groups || []).length === titles.length
  && groups.every((g, i) => txt(g.title).toUpperCase() === titles[i] && !list(g.items).length);

// Switching the datasheet type swaps the template without touching what the
// user has already written (untouched scaffolding is cleared).
export function applyVariant(us, variant) {
  const next = { ...us, variant };
  const wasTrack = us.fixture.ordering.typeKey === 'track';
  if (variant === 'track' && !wasTrack) {
    next.fixture = {
      ...us.fixture,
      ordering: { ...orderingPreset('track'), accessories: us.fixture.ordering.accessories },
      compatible: { ...us.fixture.compatible, title: 'FOR USE WITH THE FOLLOWING TRACK SYSTEMS' },
      productData: rowsOf(us.fixture.productData.rows).length
        ? us.fixture.productData
        : { ...us.fixture.productData, rows: TRACK_DATA_ROWS.map(label => ({ label, value: '' })) },
      specifications: (us.fixture.specifications || []).length
        ? us.fixture.specifications
        : TRACK_SPEC_GROUPS.map(title => ({ title, items: [] })),
      hero: { ...us.fixture.hero, placeholder: 'Track fitting render / photo' },
    };
    if (!txt(us.meta.productType) || txt(us.meta.productType).toUpperCase() === 'RECESSED DOWNLIGHT') {
      next.meta = { ...us.meta, productType: 'TRACK FITTING' };
    }
  } else if (variant !== 'track' && wasTrack) {
    const f = us.fixture;
    next.fixture = {
      ...f,
      ordering: { ...orderingPreset('downlight'), accessories: f.ordering.accessories },
      compatible: { ...f.compatible, title: 'FOR USE WITH THE FOLLOWING HOUSINGS' },
      productData: sameLabels(f.productData.rows, TRACK_DATA_ROWS) ? { ...f.productData, rows: [] } : f.productData,
      specifications: emptyGroups(f.specifications, TRACK_SPEC_GROUPS) ? [] : f.specifications,
    };
    if (txt(us.meta.productType).toUpperCase() === 'TRACK FITTING') next.meta = { ...us.meta, productType: '' };
  }
  return next;
}

export function defaultUs() {
  return { variant: 'fixture', mode: 'submittal', options: { specifierRow: true, accessories: true, headerFields: true, blankOrderingBoxes: true, photometrics: true }, logos: { secondary: '', tertiary: '' }, meta: defaultMeta(), fixture: defaultFixture(), housing: defaultHousing() };
}

const merge = (base, over) => {
  if (!over || typeof over !== 'object' || Array.isArray(over)) return over == null ? base : over;
  const out = { ...base };
  Object.keys(over).forEach(k => { out[k] = (base && typeof base[k] === 'object' && !Array.isArray(base[k]) && base[k] !== null) ? merge(base[k], over[k]) : over[k]; });
  return out;
};

// Legacy US sheets (pre-kit) carried AU-style fields; lift what we can into the new model.
function fromLegacy(s) {
  const us = defaultUs();
  const housing = (s.usType || 'product') === 'housing';
  us.variant = housing ? 'housing' : 'fixture';
  us.meta.productName = s.name || '';
  us.meta.family = s.family || '';
  us.meta.productType = s.variant || '';
  const rows = Object.entries(s.productDetails || {}).filter(([, v]) => v).map(([k, v]) => ({ label: k.replace(/_/g, ' ').toUpperCase(), value: v }))
    .concat((s.productDetailsCustom || []).filter(r => r && (r.label || r.value)));
  const groups = (s.commonSpecsCustom || []).filter(r => r && (r.label || r.value)).map(r => ({ title: r.label || '', items: String(r.value || '').split('\n') }));
  if (housing) {
    us.housing.name = s.name || ''; us.housing.catalogNumber = s.code || ''; us.housing.catalogNumberOverride = s.code || null;
    us.housing.housingData.rows = rows; us.housing.bulletGroups = groups; us.housing.hero.src = s.productImage || '';
  } else {
    us.fixture.catalogNumber = s.code || ''; us.fixture.catalogNumberOverride = s.code || null; us.fixture.productData.rows = rows; us.fixture.specifications = groups;
    us.fixture.hero.src = s.productImage || '';
    if (s.dimensionsImage) us.fixture.dimensions.drawings = [{ src: s.dimensionsImage, caption: '' }];
  }
  return us;
}

export function ensureUs(sheet) {
  if (!sheet) return defaultUs();
  if (!sheet.us) return fromLegacy(sheet);
  const us = merge(defaultUs(), sheet.us);
  us.meta.company = { ...us.meta.company, ...US_FOOTER };
  // Old sheets have segments/tables, not groups. Never overlay new preset
  // options on their saved data merely by opening them.
  ['fixture', 'housing'].forEach(part => {
    delete us[part].ordering.optionLibraryVersion;
    if (sheet.us[part]?.ordering?.optionLibraryVersion === 2) us[part].ordering.optionLibraryVersion = 2;
    const old = sheet.us[part]?.ordering;
    if (old && !old.typeKey) delete us[part].ordering.typeKey;
    if (old && !Array.isArray(old.groups)) {
      delete us[part].ordering.groups;
      us[part].ordering.example = old.example || us[part].catalogNumber || '';
    }
    us[part].ordering = restoreOrderingOptions(us[part].ordering, part);
  });
  return us;
}

// Keep the top-level library fields (name/code/family) in step with the US document.
export function syncTop(us) {
  const housingOnly = us.variant === 'housing';
  return {
    name: us.sampleName || (housingOnly ? us.housing.name : us.meta.productName),
    code: catalogNumberFor(housingOnly ? us.housing : us.fixture, housingOnly ? 'housing' : 'fixture'),
    family: us.meta.family,
    variant: us.meta.productType,
  };
}

// ---- emptiness helpers shared by preview + page selection ----
export const txt = v => (v == null ? '' : String(v)).trim();
export const list = a => (a || []).map(txt).filter(Boolean);
export const rowsOf = a => (a || []).filter(r => r && (txt(r.label) || txt(r.value)));
export const extrasOf = a => (a || []).filter(b => b && (txt(b.title) || list(b.items).length || rowsOf(b.rows).length || txt(b.text)));

export function orderingHasContent(o) {
  if (!o) return false;
  if (Array.isArray(o.groups)) return visibleOrderingGroups(o).length > 0 || list(o.notes).length > 0;
  const tables = (o.tables || []).some(t => (t.columns || []).some(c => txt(c.title) || (c.options || []).some(op => txt(op.code) || txt(op.label))));
  const acc = o.accessories && (o.accessories.rows || []).some(r => (r || []).some(txt));
  return (o.segments || []).some(sg => txt(sg.code) || txt(sg.label)) || tables || !!txt(o.example) || list(o.notes).length > 0 || !!acc;
}

export function pageKeys(us) {
  const f = us.fixture, h = us.housing, keys = [];
  if (hasFixture(us.variant)) {
    keys.push('fixture-cover');
    const specs = (f.dimensions.drawings || []).some(d => txt(d.src) || d.pending);
    if (specs) keys.push('fixture-specs');
    if (orderingHasContent(f.ordering) || extrasOf(f.extras.ordering).length || list(f.compatible.items).length || txt(f.compatible.note)) keys.push('fixture-ordering');
    if (us.options.accessories && ((f.ordering.accessories?.rows || []).some(r => (r || []).some(txt)) || extrasOf(f.extras.accessories).length)) keys.push('fixture-accessories');
    const p = f.photometrics;
    if (us.options.photometrics !== false && ((p.plots || []).some(pl => txt(pl.src) || txt(pl.title)) || ((p.lumenTable.rows || []).length && lumenTableFor(f).rows.length) || extrasOf(f.extras.photometrics).length)) keys.push('fixture-photometrics');
  }
  if (hasHousing(us.variant)) {
    keys.push('housing-cover');
    if ((h.drawings.styles || []).some(s => txt(s.code) || txt(s.name) || rowsOf(s.specs).length) || extrasOf(h.extras.drawings).length) keys.push('housing-drawings');
  }
  return keys;
}
